import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const STATUS_LABELS = { pending:'Pending', in_progress:'In Progress', awaiting_info:'Awaiting Info', escalated:'Escalated', resolved:'Resolved', closed:'Closed' };
const PRIO_COLOR = { critical:'prio-critical', high:'prio-high', medium:'prio-medium', low:'prio-low' };
const PRIO_ICONS = { critical:'🔴', high:'🟠', medium:'🔵', low:'⚪' };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [saStats, setSaStats] = useState(null);
  const [saPending, setSaPending] = useState([]);
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const isBranchManager = user?.role === 'branch_manager';
  const isAssignableStaff = ['ict_staff','ict_manager','finance_officer'].includes(user?.role);

  useEffect(() => {
    const calls = [
      api.get('/api/tickets/stats/dashboard'),
      api.get('/api/tickets?limit=6&page=1'),
    ];
    if (isBranchManager) {
      calls.push(api.get('/api/sa/stats').catch(() => ({ data: null })));
      calls.push(api.get('/api/sa?status=pending&limit=5').catch(() => ({ data: { requests: [] } })));
    }
    if (isAssignableStaff) {
      calls.push(api.get('/api/tickets?assigned_to_me=true&limit=5').catch(() => ({ data: { tickets: [] } })));
    }
    Promise.all(calls).then((results) => {
      let i = 0;
      setStats(results[i++].data);
      setRecentTickets(results[i++].data.tickets || []);
      if (isBranchManager) {
        setSaStats(results[i++]?.data || null);
        setSaPending(results[i++]?.data?.requests || []);
      }
      if (isAssignableStaff) {
        setAssignedTickets(results[i++]?.data?.tickets || []);
      }
    }).finally(() => setLoading(false));
  }, [isBranchManager, isAssignableStaff]);

  if (loading) return <div className="loading">⏳ Loading dashboard…</div>;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{greeting}, {user?.full_name?.split(' ')[0]}! 👋</h2>
        <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>
          {isBranchManager
            ? `Branch overview — ${user?.branch}`
            : "Here's what's happening on the helpdesk today."}
        </p>
      </div>

      <div className="stats-grid">
        {[
          { label: 'Open Tickets', val: stats?.open ?? 0, sub: 'Awaiting resolution', icon: '🎫', cls: 'orange', link: '/tickets?view=open' },
          { label: 'In Progress', val: stats?.in_progress ?? 0, sub: 'Being attended to', icon: '⚡', cls: 'sky', link: '/tickets?status=in_progress' },
          { label: 'Resolved Today', val: stats?.resolved_today ?? 0, sub: 'Closed successfully', icon: '✅', cls: 'green', link: '/tickets?view=resolved_today' },
          { label: 'Assigned to Me', val: stats?.assigned_to_me ?? 0, sub: stats?.assigned_to_me > 0 ? '📋 Action required' : '✓ Nothing pending', icon: '📋', cls: 'sky', link: '/tickets?assigned_to_me=true' },
          { label: 'SLA Breached', val: stats?.sla_breached ?? 0, sub: stats?.sla_breached > 0 ? '⚠️ Needs attention' : '✓ All within SLA', icon: '⏰', cls: 'red', link: '/tickets?view=sla_breached' },
        ].map(s => (
          <div key={s.label} className={`stat-card ${s.cls}`} onClick={() => navigate(s.link)} style={{ cursor: 'pointer' }}>
            <div className="stat-card-icon">{s.icon}</div>
            <div className="stat-label">{s.label}</div>
            <div className="stat-val">{s.val}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ICT/Finance staff — tickets assigned to me */}
      {isAssignableStaff && (
        <div className="card card-body" style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div className="section-title">📋 Assigned to Me</div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/tickets?assigned_to_me=true')}>View all →</button>
          </div>
          {assignedTickets.length === 0 ? (
            <div style={{ textAlign:'center', padding:'20px 0', color:'var(--muted)', fontSize:12 }}>
              ✅ No tickets currently assigned to you
            </div>
          ) : (
            assignedTickets.map(t => (
              <div key={t.id} onClick={() => navigate(`/tickets/${t.id}`)}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', background:'var(--surface)', borderRadius:8, border:'1px solid var(--border)', marginBottom:8, cursor:'pointer' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span className={`prio-dot ${PRIO_COLOR[t.priority]}`} />
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:'var(--sky-dark)', fontFamily:'monospace' }}>{t.ticket_number}</div>
                    <div style={{ fontSize:12, color:'var(--text)', maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.subject}</div>
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <span className={`badge badge-${t.status}`} style={{ fontSize:10 }}>{STATUS_LABELS[t.status]}</span>
                  <div style={{ fontSize:10, color:'var(--muted)', marginTop:3 }}>{format(new Date(t.created_at), 'dd MMM, HH:mm')}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Branch Manager — category breakdown */}
      {isBranchManager && stats?.by_category?.length > 0 && (
        <div className="card card-body" style={{ marginBottom: 16, padding: 16 }}>
          <div className="section-title" style={{ marginBottom: 12 }}>📊 {user?.branch} — Tickets by Category</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {stats.by_category.map(c => (
              <div key={c.category} style={{
                background: 'var(--surface)', borderRadius: 8, padding: '10px 12px',
                border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{c.category}</div>
                <div style={{ fontFamily: 'Sora', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{c.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Branch Manager — SA requests needing recommendation */}
      {isBranchManager && (
        <div className="card card-body" style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div className="section-title">💰 Salary Advance — Awaiting Your Recommendation</div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/sa')}>View all →</button>
          </div>
          {saStats && (
            <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
              <span style={{ background:'#FEF6E0', color:'#E09A10', padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:600 }}>
                Pending: {saStats.pending || 0}
              </span>
              <span style={{ background:'#EBF5FF', color:'#1270B8', padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:600 }}>
                Recommended: {saStats.bm_recommended || 0}
              </span>
              <span style={{ background:'#E6F7F1', color:'#0FA86A', padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:600 }}>
                Approved: {saStats.approved || 0}
              </span>
            </div>
          )}
          {saPending.length === 0 ? (
            <div style={{ textAlign:'center', padding:'20px 0', color:'var(--muted)', fontSize:12 }}>
              ✅ No requests waiting for your recommendation
            </div>
          ) : (
            saPending.map(r => (
              <div key={r.id} onClick={() => navigate(`/sa/${r.id}`)}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', background:'var(--surface)', borderRadius:8, border:'1px solid var(--border)', marginBottom:8, cursor:'pointer' }}>
                <div>
                  <div style={{ fontSize:12, fontWeight:600, color:'var(--sky-dark)', fontFamily:'monospace' }}>{r.request_number}</div>
                  <div style={{ fontSize:12, color:'var(--text)' }}>{r.account_name}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>
                    {new Intl.NumberFormat('en-NG', { style:'currency', currency:'NGN' }).format(r.amount_requested)}
                  </div>
                  <div style={{ fontSize:10, color:'var(--muted)' }}>{format(new Date(r.created_at), 'dd MMM, HH:mm')}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 290px', gap: 16 }}>
        <div>
          <div className="section-head">
            <div className="section-title">Recent Tickets</div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/tickets')}>View all →</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ID</th>
                  <th>Subject</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentTickets.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text3)', padding: 30 }}>No tickets yet</td></tr>
                )}
                {recentTickets.map(t => (
                  <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)}>
                    <td><span className="ticket-id">{t.ticket_number}</span></td>
                    <td><span style={{ display: 'block', maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12, fontWeight: 500 }}>{t.subject}</span></td>
                    <td><span className={`prio-dot ${PRIO_COLOR[t.priority]}`} />{t.priority}</td>
                    <td><span className={`badge badge-${t.status}`}>{STATUS_LABELS[t.status]}</span></td>
                    <td style={{ color: 'var(--text3)', fontSize: 11 }}>{format(new Date(t.created_at), 'dd MMM, HH:mm')}</td>
                    <td><button className="action-btn" onClick={e => { e.stopPropagation(); navigate(`/tickets/${t.id}`); }}>View →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="mini-card">
            <div className="mini-title">By Status</div>
            {(stats?.by_status || []).map(s => (
              <div key={s.status} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                <span className={`badge badge-${s.status}`}>{STATUS_LABELS[s.status] || s.status}</span>
                <span style={{ fontFamily: 'Sora', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{s.count}</span>
              </div>
            ))}
          </div>
          <div className="mini-card">
            <div className="mini-title">SLA Monitor</div>
            {[
              { label: 'Critical (4hr)', key: 'critical', color: 'var(--red)' },
              { label: 'High (8hr)', key: 'high', color: 'var(--warn)' },
              { label: 'Medium (24hr)', key: 'medium', color: 'var(--sky)' },
              { label: 'Low (48hr)', key: 'low', color: 'var(--text4)' },
            ].map(p => {
              const count = parseInt((stats?.by_priority || []).find(b => b.priority === p.key)?.count || 0);
              return (
                <div key={p.key} style={{ marginBottom: 9 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>
                    <span>{p.label}</span><span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{count} tickets</span>
                  </div>
                  <div className="sla-bar">
                    <div className="sla-fill" style={{ width: `${Math.min(count * 10, 100)}%`, background: p.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
